import { dateUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import {
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { type ComponentProps, useState } from 'react'

import { useActiveUnappliedCommands } from '../../hooks/useActiveUnappliedCommands'
import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { PostingsModal } from '../postings/PostingsModal'
import { MoneyOutReceiver } from './MoneyOutReceiver'

type Props = Pick<ComponentProps<typeof CardHeader>, 'actions'>

function MoneyOutInternal({ actions: externalActions }: Props) {
  const translate = useTranslate()
  const [isModalOpen, setIsOpenModal] = useState(false)
  const { formatWithDecimals } = useAmountFormatter()

  const { data: postings } = useHook(
    'fetchers.life.useCommitmentMoneyOutPostings',
  )()
  const hasActiveUnappliedCommand = useActiveUnappliedCommands()

  if (!postings.length && !externalActions?.length) {
    return null
  }

  const actions = postings.length
    ? [
        {
          key: 'postings',
          onClick: () => setIsOpenModal(true),
          label: translate('postings.title'),
        },
      ]
    : undefined

  const handleCloseModal = () => {
    setIsOpenModal(false)
  }

  return !hasActiveUnappliedCommand ? (
    <>
      <Card>
        <CardHeader title={translate('moneyOut.title')} actions={actions} />
        <CardBody>
          {postings.length ? (
            <Table>
              <THead>
                <Tr>
                  <Th>{translate('moneyOut.period')}</Th>
                  <Th>{translate('moneyOut.receiver')}</Th>
                  <Th>{translate('moneyOut.cover')}</Th>
                  <Th className="text-right">{translate('moneyOut.amount')}</Th>
                </Tr>
              </THead>
              <TBody>
                {postings.map(({ openPostPosting }) => (
                  <Tr key={openPostPosting._id}>
                    <Td>
                      {dateUtils.dateRangeAsStr(
                        openPostPosting.fromDueDate,
                        openPostPosting.toDueDate,
                      )}
                    </Td>
                    <Td>
                      <MoneyOutReceiver
                        dimensions={openPostPosting.dimensions}
                      />
                    </Td>
                    <Td>{openPostPosting.dimensions?.dim6}</Td>
                    <Td className="text-right">
                      {formatWithDecimals(openPostPosting.amount)}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          ) : (
            <p className="text-sm italic text-gray-300">
              {translate('noRecordsFound')}
            </p>
          )}
        </CardBody>
      </Card>
      <PostingsModal
        title={translate('moneyOut.details.modalTitle')}
        postings={postings}
        shouldShowAllDimensions
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  ) : null
}

export const MoneyOut = withLifeErrorBoundary(withSuspense(MoneyOutInternal))
